/* eslint-disable array-callback-return */
import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Col,
  Tab,
  Tabs,
  Row,
  CloseButton,
  Alert,
} from "react-bootstrap";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import {
  setInitialFarms,
  setInitialBusiness,
} from "../../configurations/redux-configurations/actions/fieldAction";
import {
  INFO,
  DANGER,
  EMPTY,
  RESOURCES_KEYS,
  FARM_BUSINESS_SELECT_STYLES,
  FARM_BUSINESS_SELECT_STYLES_ERROR,
} from "../../configurations/Constants";
import {
  ENTER_FARM_NAME,
  FARM_NAME_EXIST,
  ENTER_BUSINESS_NAME,
  BUSINESS_NAME_EXIST,
  DATA_UPDATED_SUCCESSFULLY,
  ERROR_WHILE_SAVING_DATA,
  SELECT_BUSINESS,
  SELECT_FARM,
  TRANSFER_SAME_BUSINESS_VALIDATION,
  NO_FARMS_AVAILABLE_FOR_BUSINESS,
} from "../../configurations/MessageConstants";
import MessageAlert from "../common/Alert";
import UserService from "../../api-service/UserService";
import SessionHelper from "../../helpers/SessionHelper";
import { Spinner } from "react-bootstrap";
import { StatusCodes } from "../../configurations/status-codes";

function ManageFarmBusinessModal({ closeModal: closeModalProp, onFarmBusinessUpdated }) {
  const dispatch = useDispatch();
  const loggedInUser = useSelector((state) => state.user.loggedInUser);

  const [activeTab, setActiveTab] = useState("edit");
  const [selectedBusinessToEdit, setSelectedBusinessToEdit] = useState(EMPTY);
  const [editBusinessName, setEditBusinessName] = useState(EMPTY);
  const [editFarms, setEditFarms] = useState([]);
  const [originalBusinessName, setOriginalBusinessName] = useState(EMPTY);
  const [validated, setValidated] = useState(false);
  const [invalidBusinessNameError, setInvalidBusinessNameError] = useState(false);
  const [invalidFarmNameErrors, setInvalidFarmNameErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [alertVariant, setAlertVariant] = useState(DANGER);
  const [showAlert, setShowAlert] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [transferSelectedFarms, setTransferSelectedFarms] = useState([]);
  const [transferSelectedBusiness, setTransferSelectedBusiness] = useState(EMPTY);
  const [transferNewBusinessName, setTransferNewBusinessName] = useState(EMPTY);
  const [transferShowNewBusiness, setTransferShowNewBusiness] = useState(false);
  const [transferFarmsError, setTransferFarmsError] = useState(false);
  const [transferBusinessError, setTransferBusinessError] = useState(false);
  const [transferInvalidBusinessNameError, setTransferInvalidBusinessNameError] = useState(false);
  const [isTransferUpdating, setIsTransferUpdating] = useState(false);

  const [addFarmName, setAddFarmName] = useState(EMPTY);
  const [addSelectedBusiness, setAddSelectedBusiness] = useState(EMPTY);
  const [addNewBusinessName, setAddNewBusinessName] = useState(EMPTY);
  const [addShowNewBusiness, setAddShowNewBusiness] = useState(false);
  const [addFarmNameError, setAddFarmNameError] = useState(false);
  const [addBusinessError, setAddBusinessError] = useState(false);
  const [addInvalidFarmNameError, setAddInvalidFarmNameError] = useState(false);
  const [addInvalidBusinessNameError, setAddInvalidBusinessNameError] = useState(false);
  const [isAddUpdating, setIsAddUpdating] = useState(false);
  const [resourcesBusinesses, setResourcesBusinesses] = useState([]);
  const [resourcesFarms, setResourcesFarms] = useState([]);

  const normalizeName = (val) => {
    if (val == null) return "";
    const str =
      typeof val === "object"
        ? val.name ?? val.business_name ?? val.farm_name ?? String(val)
        : String(val);
    return String(str).trim();
  };

  const businessList = useMemo(
    () =>
      resourcesBusinesses.map((business) => (
        <option key={business.businessId} value={String(business.businessId)}>
          {business.businessName}
        </option>
      )),
    [resourcesBusinesses]
  );

  const getBusinessById = (businessId) =>
    resourcesBusinesses.find((business) => String(business.businessId) === String(businessId));

  const getBusinessFarms = (businessId) => {
    const selectedBusinessId = Number(businessId);
    return resourcesFarms
      .filter((farm) => farm.businessId === selectedBusinessId)
      .map((farm) => ({
        farmId: farm.farmId,
        name: farm.farmName,
      }));
  };

  const transferFarmOptions = useMemo(() => {
    return resourcesFarms.map((farm) => ({
      value: farm.farmId,
      label: farm.farmName,
      farmId: farm.farmId,
      businessId: farm.businessId,
      businessName: farm.businessName,
    }));
  }, [resourcesFarms]);

  const transferSelectedFarmsBusinesses = useMemo(() => {
    if (!transferSelectedFarms || transferSelectedFarms.length === 0) return [];
    const businessNames = new Set();
    transferSelectedFarms.forEach((farm) => {
      if (farm.businessName) {
        businessNames.add(farm.businessName);
      }
    });
    return Array.from(businessNames);
  }, [transferSelectedFarms]);

  const syncReduxFarmBusiness = (businesses, farms) => {
    const businessNames = businesses.map((business) => business.businessName);
    const farmBusinesses = farms.map((farm) => ({
      farmName: farm.farmName,
      businessName: farm.businessName,
    }));
    dispatch(setInitialBusiness(businessNames));
    dispatch(setInitialFarms(farmBusinesses));
  };

  const normalizeResourcesKeys = (resourcesKeys) => {
    const businesses = (resourcesKeys?.businesses || [])
      .map((business) => ({
        businessId: business.business_id,
        businessName: normalizeName(business.business_name),
      }))

    const farms = (resourcesKeys?.farms_and_businesses || [])
      .map((farm) => ({
        farmId: farm.farm_id,
        farmName: normalizeName(farm.farm_name),
        businessId: farm.business_id,
        businessName: normalizeName(farm.business_name),
      }))

    return { businesses, farms };
  };

  const refreshResourcesKeys = async () => {
    const payload = {
      user_email: loggedInUser,
      keys: [RESOURCES_KEYS],
    };
    const response = await UserService.getUserFieldDetails(payload);
    const keysData = response?.data?.data?.resources;
    const normalized = normalizeResourcesKeys(keysData);
    setResourcesBusinesses(normalized.businesses);
    setResourcesFarms(normalized.farms);
    syncReduxFarmBusiness(normalized.businesses, normalized.farms);
    return normalized;
  };

  useEffect(() => {
    (async () => {
      try {
        await SessionHelper.handleRefreshToken();
        await refreshResourcesKeys();
      } catch (error) {
        if (error?.response?.status === StatusCodes.BAD_REQUEST) {
          setAlertVariant(DANGER);
          setAlertMessage(error?.response?.data?.message || ERROR_WHILE_SAVING_DATA);
          setShowAlert(true);
        } 
      }
    })();
  }, []);

  const handleBusinessSelectChange = (e) => {
    const value = e.target.value;
    setSelectedBusinessToEdit(value);
    setInvalidBusinessNameError(false);
    setInvalidFarmNameErrors({});
    if (value !== EMPTY) {
      const selectedBusiness = getBusinessById(value);
      const farms = getBusinessFarms(value);
      setEditBusinessName(selectedBusiness?.businessName || EMPTY);
      setOriginalBusinessName(selectedBusiness?.businessName || EMPTY);
      setEditFarms(farms);
    } else {
      setEditBusinessName(EMPTY);
      setOriginalBusinessName(EMPTY);
      setEditFarms([]);
    }
  };

  const handleBusinessNameChange = (e) => {
    setEditBusinessName(e.target.value);
    setInvalidBusinessNameError(false);
  };

  const handleFarmNameChange = (farmId, value) => {
    setEditFarms((prev) =>
      prev.map((farm) => (farm.farmId === farmId ? { ...farm, name: value } : farm))
    );
    setInvalidFarmNameErrors((prev) => {
      const next = { ...prev };
      delete next[farmId];
      return next;
    });
  };

  const closeModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    closeModalProp();
  };

  const closeAlert = () => {
    setShowAlert(false);
    setAlertMessage(EMPTY);
  };

  const handleTabSelect = (key) => {
    const isAnySubmitInProgress = isUpdating || isAddUpdating || isTransferUpdating;
    if (isAnySubmitInProgress || key === activeTab) return;

    resetEditTab();
    resetAddTab();
    resetTransferTab();
    setActiveTab(key);
    closeAlert();
  };

  const resetEditTab = () => {
    setSelectedBusinessToEdit(EMPTY);
    setEditBusinessName(EMPTY);
    setEditFarms([]);
    setOriginalBusinessName(EMPTY);
    setValidated(false);
    setInvalidBusinessNameError(false);
    setInvalidFarmNameErrors({});
  };

  const resetTransferTab = () => {
    setTransferSelectedFarms([]);
    setTransferSelectedBusiness(EMPTY);
    setTransferNewBusinessName(EMPTY);
    setTransferShowNewBusiness(false);
    setTransferFarmsError(false);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);
  };

  const resetAddTab = () => {
    setAddFarmName(EMPTY);
    setAddSelectedBusiness(EMPTY);
    setAddNewBusinessName(EMPTY);
    setAddShowNewBusiness(false);
    setAddFarmNameError(false);
    setAddBusinessError(false);
    setAddInvalidFarmNameError(false);
    setAddInvalidBusinessNameError(false);
  };

  const applyLocalResourcesState = (businesses, farms) => {
    setResourcesBusinesses(businesses);
    setResourcesFarms(farms);
    syncReduxFarmBusiness(businesses, farms);
  };

  const handleAddNewBusinessClick = () => {
    setAddShowNewBusiness(true);
    setAddSelectedBusiness(EMPTY);
    setAddBusinessError(false);
    setAddInvalidBusinessNameError(false);
  };

  const handleAddBackToSelectBusiness = () => {
    setAddShowNewBusiness(false);
    setAddNewBusinessName(EMPTY);
    setAddBusinessError(false);
    setAddInvalidBusinessNameError(false);
  };

  const handleAddFarmNameChange = (e) => {
    setAddFarmName(e.target.value);
    setAddFarmNameError(false);
    setAddInvalidFarmNameError(false);
  };

  const handleAddBusinessSelectChange = (e) => {
    setAddSelectedBusiness(e.target.value);
    setAddBusinessError(false);
  };

  const handleAddNewBusinessNameChange = (e) => {
    setAddNewBusinessName(e.target.value);
    setAddInvalidBusinessNameError(false);
    setAddBusinessError(false);
  };

  const handleAddFarm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAddFarmNameError(false);
    setAddBusinessError(false);
    setAddInvalidFarmNameError(false);
    setAddInvalidBusinessNameError(false);

    const farmNameTrimmed = addFarmName.trim();
    const selectedBusiness = getBusinessById(addSelectedBusiness);
    const businessName = addShowNewBusiness
      ? addNewBusinessName.trim()
      : selectedBusiness?.businessName || EMPTY;

    let hasError = false;

    if (farmNameTrimmed === EMPTY) {
      setAddFarmNameError(true);
      hasError = true;
    }

    if (businessName === EMPTY) {
      setAddBusinessError(true);
      hasError = true;
    }

    if (hasError) return;

    for (let i = 0; i < resourcesFarms.length; i++) {
      if (resourcesFarms[i].farmName.toLowerCase() === farmNameTrimmed.toLowerCase()) {
        setAddInvalidFarmNameError(true);
        return;
      }
    }

    if (addShowNewBusiness) {
      for (let i = 0; i < resourcesBusinesses.length; i++) {
        if (resourcesBusinesses[i].businessName.toLowerCase() === businessName.toLowerCase()) {
          setAddInvalidBusinessNameError(true);
          return;
        }
      }
    }

    setIsAddUpdating(true);

    const payload = {
      business_name: businessName,
      farm_name: farmNameTrimmed,
    };

    try {
      await SessionHelper.handleRefreshToken();
      const addResponse = await UserService.addBusinessFarm(payload);
      const responseData = addResponse?.data?.data || {};
      const responseBusinessId = responseData.business_id;
      const responseFarmId = responseData.farm_id;
      const targetBusinessId = addShowNewBusiness
        ? responseBusinessId
        : Number(addSelectedBusiness);

      if (responseFarmId == null || targetBusinessId == null) {
        setShowAlert(true);
        setAlertVariant(DANGER);
        setAlertMessage(ERROR_WHILE_SAVING_DATA);
        return;
      }

      const nextBusinesses = addShowNewBusiness
        ? [
            ...resourcesBusinesses,
            {
              businessId: responseBusinessId,
              businessName,
            },
          ]
        : resourcesBusinesses;

      const nextFarms = [
        ...resourcesFarms,
        {
          farmId: responseFarmId,
          farmName: farmNameTrimmed,
          businessId: targetBusinessId,
          businessName,
        },
      ];
      applyLocalResourcesState(nextBusinesses, nextFarms);

      setShowAlert(true);
      setAlertVariant(INFO);
      setAlertMessage(DATA_UPDATED_SUCCESSFULLY);
      resetAddTab();

      if (onFarmBusinessUpdated) {
        await onFarmBusinessUpdated();
      }
    } catch (error) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(error?.response?.data?.message || ERROR_WHILE_SAVING_DATA);
    } finally {
      setIsAddUpdating(false);
    }
  };

  const handleTransferFarmsChange = (selected) => {
    setTransferSelectedFarms(selected || []);
    setTransferFarmsError(false);
    closeAlert();
  };

  const handleTransferBusinessChange = (e) => {
    setTransferSelectedBusiness(e.target.value);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);
    closeAlert();
  };

  const handleTransferAddNewBusinessClick = () => {
    setTransferShowNewBusiness(true);
    setTransferSelectedBusiness(EMPTY);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);
  };

  const handleTransferBackToSelectBusiness = () => {
    setTransferShowNewBusiness(false);
    setTransferNewBusinessName(EMPTY);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);
  };

  const handleTransferNewBusinessNameChange = (e) => {
    setTransferNewBusinessName(e.target.value);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);
    closeAlert();
  };

  const handleTransferUpdate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTransferFarmsError(false);
    setTransferBusinessError(false);
    setTransferInvalidBusinessNameError(false);

    const hasFarms = transferSelectedFarms && transferSelectedFarms.length > 0;
    const transferBusinessName = transferShowNewBusiness
      ? transferNewBusinessName.trim()
      : EMPTY;
    const hasBusiness = transferShowNewBusiness
      ? transferBusinessName !== EMPTY
      : transferSelectedBusiness !== EMPTY;

    if (!hasFarms) setTransferFarmsError(true);
    if (!hasBusiness) setTransferBusinessError(true);
    if (!hasFarms || !hasBusiness) return;

    const targetBusinessId = transferShowNewBusiness
      ? null
      : Number(transferSelectedBusiness);
    const farmIds = transferSelectedFarms.map((farm) => farm.farmId).filter((id) => id != null);
    const isSameTargetBusinessSelected = transferShowNewBusiness
      ? false
      : transferSelectedFarms.some((farm) => {
          if (farm.businessId != null) return Number(farm.businessId) === targetBusinessId;
          const sourceFarm = resourcesFarms.find((resourceFarm) => resourceFarm.farmId === farm.farmId);
          return sourceFarm ? Number(sourceFarm.businessId) === targetBusinessId : false;
        });

    const hasAllIds = farmIds.length === transferSelectedFarms.length;

    if (!hasAllIds) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(ERROR_WHILE_SAVING_DATA);
      return;
    }

    if (!transferShowNewBusiness && !Number.isFinite(targetBusinessId)) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(ERROR_WHILE_SAVING_DATA);
      return;
    }

    if (transferShowNewBusiness) {
      const isDuplicateBusiness = resourcesBusinesses.some(
        (business) =>
          business.businessName.trim().toLowerCase() === transferBusinessName.toLowerCase()
      );
      if (isDuplicateBusiness) {
        setTransferInvalidBusinessNameError(true);
        return;
      }
    }

    if (isSameTargetBusinessSelected) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(TRANSFER_SAME_BUSINESS_VALIDATION);
      return;
    }

    setIsTransferUpdating(true);

    const payload = {
      farm_ids: farmIds,
    };
    if (transferShowNewBusiness) {
      payload.target_business_name = transferBusinessName;
    } else {
      payload.target_business_id = targetBusinessId;
    }

    try {
      await SessionHelper.handleRefreshToken();
      await UserService.transferFarms(payload);
      if (transferShowNewBusiness) {
        await refreshResourcesKeys();
      } else {
        const targetBusiness = getBusinessById(transferSelectedBusiness);
        const targetBusinessName = targetBusiness?.businessName || EMPTY;
        const selectedFarmIds = new Set(farmIds);
        const updatedFarms = resourcesFarms.map((farm) =>
          selectedFarmIds.has(farm.farmId)
            ? {
                ...farm,
                businessId: targetBusinessId,
                businessName: targetBusinessName,
              }
            : farm
        );
        applyLocalResourcesState(resourcesBusinesses, updatedFarms);
      }

      setShowAlert(true);
      setAlertVariant(INFO);
      setAlertMessage(DATA_UPDATED_SUCCESSFULLY);
      resetTransferTab();

      if (onFarmBusinessUpdated) {
        await onFarmBusinessUpdated();
      }
    } catch (error) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(error?.response?.data?.message || ERROR_WHILE_SAVING_DATA);
    } finally {
      setIsTransferUpdating(false);
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setValidated(true);
    setInvalidBusinessNameError(false);
    setInvalidFarmNameErrors({});

    if (selectedBusinessToEdit === EMPTY) {
      return;
    }

    let hasError = false;
    let shouldSetBusinessError = false;
    const nextFarmErrors = {};

    if (editBusinessName.trim() === EMPTY) {
      shouldSetBusinessError = true;
      hasError = true;
    }

    const farmNames = editFarms.map((farm) => farm.name.trim());
    farmNames.forEach((name, idx) => {
      if (name === EMPTY) {
        nextFarmErrors[editFarms[idx].farmId] = true;
        hasError = true;
      }
    });

    const lowerFarmNames = farmNames.map((name) => name.toLowerCase());
    const duplicateFarmNameSet = new Set();
    lowerFarmNames.forEach((name, idx) => {
      if (name === EMPTY) return;
      if (lowerFarmNames.indexOf(name) !== idx) {
        duplicateFarmNameSet.add(name);
      }
    });
    if (duplicateFarmNameSet.size > 0) {
      lowerFarmNames.forEach((name, idx) => {
        if (duplicateFarmNameSet.has(name)) {
          nextFarmErrors[editFarms[idx].farmId] = true;
        }
      });
      hasError = true;
    }

    const selectedBusinessId = Number(selectedBusinessToEdit);
    if (editBusinessName.trim() !== originalBusinessName.trim()) {
      const isDuplicateBusiness = resourcesBusinesses.some(
        (business) =>
          business.businessId !== selectedBusinessId &&
          business.businessName.trim().toLowerCase() ===
            editBusinessName.trim().toLowerCase()
      );
      if (isDuplicateBusiness) {
        shouldSetBusinessError = true;
        hasError = true;
      }
    }

    const otherBusinessFarmNames = resourcesFarms
      .filter((farm) => farm.businessId !== selectedBusinessId)
      .map((farm) => farm.farmName.toLowerCase());

    for (let i = 0; i < farmNames.length; i++) {
      const newName = farmNames[i].toLowerCase();
      if (otherBusinessFarmNames.includes(newName)) {
        nextFarmErrors[editFarms[i].farmId] = true;
        hasError = true;
      }
    }

    if (shouldSetBusinessError) {
      setInvalidBusinessNameError(true);
    }
    if (Object.keys(nextFarmErrors).length > 0) {
      setInvalidFarmNameErrors(nextFarmErrors);
    }
    if (hasError) {
      return;
    }

    setIsUpdating(true);
    applyUpdates();
  };

  const applyUpdates = async () => {
    await SessionHelper.handleRefreshToken();

    const businessId = Number(selectedBusinessToEdit);
    const farmsWithIds = editFarms.filter((farm) => farm.farmId != null);
    const hasAllIds =
      Number.isFinite(businessId) &&
      farmsWithIds.length === editFarms.length;

    if (!hasAllIds) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(ERROR_WHILE_SAVING_DATA);
      setIsUpdating(false);
      return;
    }

    const payload = {
      business_id: businessId,
      business_name: editBusinessName.trim(),
      farms: editFarms.map((farm) => ({
        farm_id: farm.farmId,
        farm_name: farm.name.trim(),
      })),
    };

    try {
      await UserService.updateBusinessFarms(payload);
      const updatedBusinesses = resourcesBusinesses.map((business) =>
        business.businessId === businessId
          ? { ...business, businessName: editBusinessName.trim() }
          : business
      );
      const updatedFarms = resourcesFarms
        .filter((farm) => farm.businessId !== businessId)
        .concat(
          editFarms.map((farm) => ({
            farmId: farm.farmId,
            farmName: farm.name.trim(),
            businessId,
            businessName: editBusinessName.trim(),
          }))
        );
      applyLocalResourcesState(updatedBusinesses, updatedFarms);

      setShowAlert(true);
      setAlertVariant(INFO);
      setAlertMessage(DATA_UPDATED_SUCCESSFULLY);
      resetEditTab();

      if (onFarmBusinessUpdated) {
        await onFarmBusinessUpdated();
      }
    } catch (error) {
      setShowAlert(true);
      setAlertVariant(DANGER);
      setAlertMessage(error?.response?.data?.message || ERROR_WHILE_SAVING_DATA);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal
      show
      className="field-details"
      onHide={() => { }}
      backdrop={false}
    >
      <Modal.Header>
        <Modal.Title>Manage Farm/Business</Modal.Title>
        <CloseButton
          className="close field-details-close"
          onClick={closeModal}
        />
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => handleTabSelect(k)}
          transition={false}
          id="manageFarmBusinessTab"
        >
          <Tab eventKey="edit" title="Edit">
            <div className="manage-farm-business-edit mt-3">
              {showAlert && (
                <MessageAlert
                  key="manageFarmBusinessAlert"
                  showAlert={showAlert}
                  variant={alertVariant}
                  closeAlert={closeAlert}
                  alertMessage={alertMessage}
                  dismissible
                />
              )}
              <Form id="manageFarmBusinessForm" noValidate onSubmit={handleUpdate}>
                <Row className="field-details-row mb-3">
                  <Form.Group as={Col} controlId="selectBusinessToEdit">
                    <Form.Label>
                      Select a business to edit<span>*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedBusinessToEdit}
                      onChange={handleBusinessSelectChange}
                      required
                      className={
                        validated && selectedBusinessToEdit === EMPTY
                          ? "is-invalid invalidcontrol"
                          : EMPTY
                      }
                    >
                      <option value={EMPTY}>-- Select a Business --</option>
                      {businessList}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {SELECT_BUSINESS}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                {selectedBusinessToEdit !== EMPTY && (
                  <>
                    <Row className="field-details-row mb-3">
                      <Form.Group as={Col} controlId="editBusinessName">
                        <Form.Label>
                          Business<span>*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          maxLength="40"
                          value={editBusinessName}
                          onChange={handleBusinessNameChange}
                          required
                          className={
                            invalidBusinessNameError
                              ? "is-invalid invalidcontrol"
                              : EMPTY
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {editBusinessName === EMPTY || editBusinessName.trim() === EMPTY
                            ? ENTER_BUSINESS_NAME
                            : BUSINESS_NAME_EXIST}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Row>

                    <Row className="field-details-row mb-3">
                      <Form.Group as={Col} controlId="editFarms">
                        <Form.Label>
                          Farms{editFarms.length > 0 && <span>*</span>}
                        </Form.Label>
                        {editFarms.length === 0 ? (
                          <div className="text-muted no-farms-for-business-message">
                            {NO_FARMS_AVAILABLE_FOR_BUSINESS}
                          </div>
                        ) : (
                          editFarms.map((farm) => (
                            <Form.Group key={farm.farmId} className="mb-2">
                              <Form.Control
                                type="text"
                                maxLength="40"
                                value={farm.name}
                                onChange={(e) =>
                                  handleFarmNameChange(farm.farmId, e.target.value)
                                }
                                required
                                className={
                                  invalidFarmNameErrors[farm.farmId]
                                    ? "is-invalid invalidcontrol"
                                    : EMPTY
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {farm.name === EMPTY || farm.name.trim() === EMPTY
                                  ? ENTER_FARM_NAME
                                  : FARM_NAME_EXIST}
                              </Form.Control.Feedback>
                            </Form.Group>
                          ))
                        )}
                      </Form.Group>
                    </Row>
                  </>
                )}
              </Form>
            </div>
          </Tab>
          <Tab eventKey="add" title="Add">
            <div className="manage-farm-business-add mt-3">
              {showAlert && (
                <MessageAlert
                  key="addAlert"
                  showAlert={showAlert}
                  variant={alertVariant}
                  closeAlert={closeAlert}
                  alertMessage={alertMessage}
                  dismissible
                />
              )}
              <Form
                id="manageFarmBusinessAddForm"
                noValidate
                onSubmit={handleAddFarm}
              >
                <Row className="field-details-row mb-3">
                  <Form.Group as={Col} controlId="addFarmName">
                    <Form.Label>
                      Farm Name<span>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      maxLength="40"
                      name="addFarmName"
                      value={addFarmName}
                      onChange={handleAddFarmNameChange}
                      required
                      className={
                        addFarmNameError || addInvalidFarmNameError
                          ? "is-invalid invalidcontrol"
                          : EMPTY
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {addFarmName === EMPTY || addFarmName.trim() === EMPTY
                        ? ENTER_FARM_NAME
                        : FARM_NAME_EXIST}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row className="field-details-row mb-3">
                  <Form.Group
                    as={Col}
                    controlId="addSelectBusiness"
                  >
                    <Form.Label>
                      Select Business<span>*</span>
                    </Form.Label>
                    <Form.Select
                      value={addSelectedBusiness}
                      onChange={handleAddBusinessSelectChange}
                      disabled={addShowNewBusiness}
                      required={!addShowNewBusiness}
                      className={
                        !addShowNewBusiness && addBusinessError
                          ? "is-invalid invalidcontrol"
                          : EMPTY
                      }
                    >
                      <option value={EMPTY}>-- Select a Business --</option>
                      {businessList}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {SELECT_BUSINESS}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Col className="col-1 ps-1">
                    {addShowNewBusiness ? (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0"
                        onClick={handleAddBackToSelectBusiness}
                        title="Back to select business"
                        aria-label="Back to select business"
                      >
                        <i className="fa fa-long-arrow-left add-farm-business-wdp-btn" aria-hidden="true" />
                      </button>
                    ) : (
                      <i
                        className="fa fa-plus-circle add-farm-business-wdp-btn"
                        onClick={handleAddNewBusinessClick}
                        title="Add new business"
                        aria-label="Add new business"
                      />
                    )}
                  </Col>
                </Row>
                {addShowNewBusiness && (
                  <Row className="field-details-row mb-3">
                    <Form.Group as={Col} controlId="addNewBusinessName">
                      <Form.Label>
                        New Business<span>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        maxLength="40"
                        name="addNewBusinessName"
                        value={addNewBusinessName}
                        onChange={handleAddNewBusinessNameChange}
                        required
                        className={
                          addInvalidBusinessNameError ||
                          (addShowNewBusiness && addBusinessError)
                            ? "is-invalid invalidcontrol"
                            : EMPTY
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {addNewBusinessName === EMPTY || addNewBusinessName.trim() === EMPTY
                          ? ENTER_BUSINESS_NAME
                          : BUSINESS_NAME_EXIST}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                )}
              </Form>
            </div>
          </Tab>
          <Tab eventKey="transfer" title="Transfer">
            <div className="manage-farm-business-transfer mt-3">
              {showAlert && (
                <MessageAlert
                  key="transferAlert"
                  showAlert={showAlert}
                  variant={alertVariant}
                  closeAlert={closeAlert}
                  alertMessage={alertMessage}
                  dismissible
                />
              )}
              <Form
                id="manageFarmBusinessTransferForm"
                noValidate
                onSubmit={handleTransferUpdate}
              >
                <Row className="field-details-row mb-3">
                  <Form.Group as={Col} controlId="transferFarms">
                    <Form.Label>
                      Select the farms to be transferred<span>*</span>
                    </Form.Label>
                    <Select
                      isMulti
                      name="transferFarms"
                      options={transferFarmOptions}
                      value={transferSelectedFarms}
                      className={`basic-multi-select ${transferFarmsError ? "is-invalid" : ""}`}
                      classNamePrefix="transfer-farms"
                      onChange={handleTransferFarmsChange}
                      placeholder="Select Farms"
                      styles={{
                        ...FARM_BUSINESS_SELECT_STYLES,
                        ...(transferFarmsError && {
                          control: (base) => ({
                            ...base,
                            ...FARM_BUSINESS_SELECT_STYLES_ERROR,
                          }),
                        }),
                      }}
                    />
                    {transferFarmsError && (
                      <div className="invalid-feedback d-block">
                        {SELECT_FARM}
                      </div>
                    )}
                  </Form.Group>
                </Row>

                {transferSelectedFarms && transferSelectedFarms.length > 0 && (
                  <Alert variant="info" className="mb-3 d-flex align-items-start">
                    <i className="fa fa-info-circle me-2 mt-1" aria-hidden="true" />
                    <div className="w-100">
                      <div>Selected farms currently belong to the following business:</div>
                      {transferSelectedFarmsBusinesses.length > 0 && (
                        <div className="fw-bold mt-1" style={{ fontSize: "0.65rem" }}>
                          {transferSelectedFarmsBusinesses.join(", ")}
                        </div>
                      )}
                    </div>
                  </Alert>
                )}

                <Row className="field-details-row mb-3">
                  <Form.Group as={Col} controlId="transferBusiness">
                    <Form.Label>
                      Select the business to transfer to<span>*</span>
                    </Form.Label>
                    <Form.Select
                      value={transferSelectedBusiness}
                      onChange={handleTransferBusinessChange}
                      disabled={transferShowNewBusiness}
                      required={!transferShowNewBusiness}
                      className={
                        !transferShowNewBusiness && transferBusinessError
                          ? "is-invalid invalidcontrol"
                          : EMPTY
                      }
                    >
                      <option value={EMPTY}>-- Select a Business --</option>
                      {businessList}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {SELECT_BUSINESS}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Col className="col-1 ps-1">
                    {transferShowNewBusiness ? (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0"
                        onClick={handleTransferBackToSelectBusiness}
                        title="Back to select business"
                        aria-label="Back to select business"
                      >
                        <i
                          className="fa fa-long-arrow-left add-farm-business-wdp-btn"
                          aria-hidden="true"
                        />
                      </button>
                    ) : (
                      <i
                        className="fa fa-plus-circle add-farm-business-wdp-btn"
                        onClick={handleTransferAddNewBusinessClick}
                        title="Add new business"
                        aria-label="Add new business"
                      />
                    )}
                  </Col>
                </Row>
                {transferShowNewBusiness && (
                  <Row className="field-details-row mb-3">
                    <Form.Group as={Col} controlId="transferNewBusinessName">
                      <Form.Label>
                        Enter the new business to transfer to<span>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        maxLength="40"
                        name="transferNewBusinessName"
                        value={transferNewBusinessName}
                        onChange={handleTransferNewBusinessNameChange}
                        required
                        className={
                          transferInvalidBusinessNameError ||
                          (transferShowNewBusiness && transferBusinessError)
                            ? "is-invalid invalidcontrol"
                            : EMPTY
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {transferNewBusinessName === EMPTY ||
                        transferNewBusinessName.trim() === EMPTY
                          ? ENTER_BUSINESS_NAME
                          : BUSINESS_NAME_EXIST}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                )}
              </Form>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer className="justify-content-start">
        {activeTab === "add" && (
          <Button
            type="submit"
            form="manageFarmBusinessAddForm"
            variant="primary"
            disabled={isAddUpdating}
          >
            {isAddUpdating ? (
              <div>
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                <span>Adding...</span>
              </div>
            ) : "Add"}
          </Button>
        )}
        {activeTab === "edit" && (
          <Button
            type="submit"
            form="manageFarmBusinessForm"
            variant="primary"
            disabled={isUpdating}
          >
            {isUpdating ? (
              <div>
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                <span>Updating...</span>
              </div>
            ) : "Update"}
          </Button>
        )}
        {activeTab === "transfer" && (
          <Button
            type="submit"
            form="manageFarmBusinessTransferForm"
            variant="primary"
            disabled={isTransferUpdating}
          >
            {isTransferUpdating ? (
              <div>
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                <span>Transferring...</span>
              </div>
            ) : "Transfer"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ManageFarmBusinessModal;
